import { Slideshow } from '@theme/slideshow';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * Mobile-only carousel for the generic section ("Carousel on mobile" setting).
 *
 * Every direct child of the scroller — i.e. each top-level block of the section —
 * becomes a slide. Extends the theme Slideshow so pagination (dots / counter),
 * scroll syncing, click-to-select and drag behave exactly like other carousels.
 *
 * On desktop the row lays out as before; the component is disabled there so the
 * slideshow's mousedown handling never interferes with text selection.
 *
 * @extends {Slideshow}
 */
class SectionCarousel extends Slideshow {
  connectedCallback() {
    const scroller = this.querySelector('[ref="scroller"]');

    if (scroller) {
      for (const child of scroller.children) {
        // Blocks may emit inline <script>/<style> siblings next to their markup — those are not slides.
        if (child.matches('script, style, template, link')) continue;
        if (!child.hasAttribute('ref')) child.setAttribute('ref', 'slides[]');
      }
    }

    super.connectedCallback();

    this.#syncDisabled();
    this.#syncDots();
    mediaQueryLarge.addEventListener('change', this.#syncDisabled);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#syncDisabled);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    super.disconnectedCallback();
  }

  #syncDisabled = () => {
    this.disabled = mediaQueryLarge.matches || this.isNested;
  };

  /** Pagination is rendered in Liquid from the block count; hide dots for blocks that rendered hidden. */
  #syncDots() {
    const count = this.slides?.length ?? 0;
    this.refs.dots?.forEach((dot, index) => {
      if (dot.parentElement) dot.parentElement.hidden = index >= count;
    });
  }

  /**
   * Theme editor: bring the selected block's slide into view so it never looks "missing" on mobile.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const index = this.slides?.findIndex((slide) => slide.contains(event.target)) ?? -1;
    if (index >= 0) this.select(index, undefined, { animate: false });
  };
}

if (!customElements.get('section-carousel')) {
  customElements.define('section-carousel', SectionCarousel);
}
